import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight } from 'lucide-react';
import api, { formatPrice } from '../api';
import { categories } from '../mock';

const catName = (id) => categories.find(c => c.id === id)?.name || id;

export default function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState('');
  const [products, setProducts] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    setQ('');
    api.get('/products').then(r => setProducts(r.data)).catch(() => setProducts([]));
    setTimeout(() => inputRef.current?.focus(), 50);
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [open, onClose]);

  if (!open) return null;

  const term = q.trim().toLowerCase();
  const results = term
    ? products.filter(p => [p.name, p.category, catName(p.category), p.color].filter(Boolean).some(v => v.toLowerCase().includes(term))).slice(0, 8)
    : [];

  const go = (path) => { onClose(); navigate(path); };

  return (
    <div data-testid="search-overlay" className="fixed inset-0 z-[70] bg-[#1a1a1a]/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-[#F4EEE4] border-b border-black/10 shadow-2xl fade-up" onClick={e => e.stopPropagation()}>
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-center gap-4 border-b border-black/30 pb-3">
            <Search className="w-5 h-5 text-[#1a1a1a]/60" />
            <input
              ref={inputRef}
              data-testid="search-input"
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && results[0]) go(`/product/${results[0].id}`); }}
              placeholder="Search bags, wallets, desk goods…"
              className="flex-1 bg-transparent outline-none font-serif-display text-2xl md:text-3xl placeholder:text-[#1a1a1a]/30"
            />
            <button data-testid="search-close-btn" onClick={onClose} aria-label="Close search"><X className="w-5 h-5" /></button>
          </div>

          {!term && (
            <div className="mt-6 flex flex-wrap gap-3">
              {categories.map(c => (
                <button key={c.id} data-testid={`search-cat-${c.id}`} onClick={() => go(`/shop/${c.id}`)} className="text-[11px] tracking-widest-plus uppercase border border-black/20 rounded-full px-4 py-2 hover:border-[#C6410E] hover:text-[#C6410E] transition-colors">
                  {c.name}
                </button>
              ))}
            </div>
          )}

          {term && (
            <ul data-testid="search-results" className="mt-6 divide-y divide-black/10 max-h-[60vh] overflow-y-auto">
              {results.map(p => (
                <li key={p.id}>
                  <button data-testid={`search-result-${p.id}`} onClick={() => go(`/product/${p.id}`)} className="w-full flex items-center gap-4 py-3 text-left group">
                    <div className="w-14 h-16 overflow-hidden bg-[#E9E1D4] shrink-0">
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-serif-display text-lg truncate group-hover:text-[#C6410E] transition-colors">{p.name}</p>
                      <p className="text-[11px] tracking-widest-plus uppercase text-[#1a1a1a]/50">{catName(p.category)} · {p.color}</p>
                    </div>
                    <span className="text-sm">{formatPrice(p.price)}</span>
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                </li>
              ))}
              {results.length === 0 && (
                <li data-testid="search-no-results" className="py-10 text-center font-serif-display text-xl text-[#1a1a1a]/50">Nothing found for “{q}”.</li>
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
